import * as React from 'react';
import { memo, Ref } from 'react';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { addModifiers } from '../../../shared/helpers';
import { localeData } from '../../../locales/data';
import classcat from 'classcat';
import { VALIDATION_DIVIDER_STRING } from '../wizard/validations';
import { Control, UseControllerReturn, useFormState } from 'react-hook-form';

export interface HookFieldSetProps {
  label?: string;
  id?: string;
  required?: boolean;
  modifiers?: string[];
  children: React.ReactNode;
  controller: UseControllerReturn<any>;
  errorValues?: any;
  className?: string;
  containerRef?: Ref<any>;
  control: Control<any>;
  keepErrorSpace?: boolean;
}

export const useFieldError = ({ control, name }: { control: Control<any>; name: string }) => {
  const formState = useFormState({ control, name, exact: true });

  const error = formState.errors[name]?.message as string | undefined;
  return error;
};

export const HookFieldError = memo(
  ({
    controller,
    values,
    control,
    keepErrorSpace = true,
  }: {
    controller: UseControllerReturn<any, any>;
    values?: any;
    control: Control<any>;
    keepErrorSpace?: boolean;
  }) => {
    const errors = useFieldError({ control, name: controller.field.name });

    const error = errors?.split?.(VALIDATION_DIVIDER_STRING)?.[0];
    if (!error && !keepErrorSpace) {
      return null;
    }
    return (
      <div className="input__error">
        <div className="error">
          {error && <FormattedMessageHelper values={values} tagName="span" id={error} defaultMessage={localeData.en[error]} />}
        </div>
      </div>
    );
  }
);

export const HookFieldSet = memo(
  ({
    label,
    control,
    id,
    modifiers = [],
    required = false,
    children,
    controller,
    errorValues,
    className,
    containerRef,
    keepErrorSpace,
  }: HookFieldSetProps) => {
    return (
      <div ref={containerRef} className={classcat([addModifiers('form__field-set', modifiers), className])}>
        {label && (
          <label htmlFor={id ? id : ''} className="form__label">
            <FormattedMessageHelper tagName="span" id={label} defaultMessage={localeData.en[label]} />
            {required && <sup>*</sup>}
          </label>
        )}
        {children}
        <HookFieldError keepErrorSpace={keepErrorSpace} control={control} values={errorValues} controller={controller} />
      </div>
    );
  }
);
